/**
 * ZoneMapAdapter — thin layer between ZoneScene and the map builders.
 *
 * Routes to the procedural builder (glade-style iso) or the Tiled map renderer
 * depending on the zone config type. The Tiled renderer is now projection-aware:
 * iso zones get diamond-tile placement + dock decorations, top-down zones get
 * axis-aligned placement.
 *
 * For procedural zones, the caller must supply a `ZoneGrid` from the
 * server's channel-join reply — the builder no longer derives terrain
 * locally. See `Mythglade.World.Terrain` (server-side) for the producing
 * module.
 */

import Phaser from "phaser";
import type { ZoneGrid } from "@/net/types";
import { TILE, currentProjection, worldToScreen, type ScreenPos } from "./Iso";
import { buildProceduralIsoMap, type IsoMap } from "./MapBuilder";
import { CollisionMap } from "./CollisionMap";
import type { ZoneConfig, ZoneTransition } from "./ZoneManifest";

const DOCK_TEXTURE_KEY = "iso-dock";

export interface ZoneMapResult {
  /** Everything the legacy builder returns. */
  map: IsoMap;
  /** Transition points declared in the manifest (empty if no config). */
  transitions: ZoneTransition[];
  /** Spawn position from the server-authoritative `ZoneGrid` (or manifest fallback for tiled). */
  spawnPoint: [number, number];
}

interface TiledLayer {
  name: string;
  type: string;
  data?: number[];
  width: number;
  height: number;
}

interface TiledMap {
  width: number;
  height: number;
  layers: TiledLayer[];
}

interface CollisionData {
  width: number;
  height: number;
  blocked: boolean[][];
}

export function buildZoneMap(
  scene: Phaser.Scene,
  origin: ScreenPos,
  zoneConfig: ZoneConfig | undefined,
  zoneGrid: ZoneGrid,
): ZoneMapResult {
  if (zoneConfig?.type === "tiled") {
    return buildTiledMap(scene, origin, zoneConfig, zoneGrid);
  }

  // ── Procedural path (iso glade-style) ──
  const map = buildProceduralIsoMap(scene, origin, zoneGrid);

  if (!zoneConfig) {
    return { map, transitions: [], spawnPoint: zoneGrid.spawn_point };
  }

  ensureDockTexture(scene);
  placeDockTiles(scene, origin, zoneConfig.transitions);

  return {
    map,
    transitions: zoneConfig.transitions,
    spawnPoint: zoneGrid.spawn_point,
  };
}

/* ───── Tiled map renderer ───── */

const RENDER_LAYERS = ["ground", "ground-detail", "props", "foreground"];

function buildTiledMap(
  scene: Phaser.Scene,
  origin: ScreenPos,
  zoneConfig: ZoneConfig,
  zoneGrid: ZoneGrid,
): ZoneMapResult {
  const projection = currentProjection();
  const isIso = projection.kind === "iso";
  const halfW = projection.tileWidth / 2;

  const gridSize = zoneConfig.gridSize;
  const tmj = scene.cache.json.get("tmj:" + zoneConfig.tmj) as TiledMap | null;
  const collisionData = scene.cache.json.get("collision:" + zoneConfig.collision) as CollisionData | null;
  const tilesetKey = zoneConfig.tilesetKey
    ?? (zoneConfig.tilesets?.[0] ? "tileset:" + zoneConfig.tilesets[0] : undefined);

  // Prefer the server-authoritative blocked grid; fall back to the
  // packed collision JSON if zoneGrid is missing it (defensive — server
  // always supplies one for tiled zones too).
  const blockedGrid: boolean[][] =
    (zoneGrid.blocked?.length ? zoneGrid.blocked : undefined)
    ?? collisionData?.blocked
    ?? Array.from({ length: gridSize }, () => new Array<boolean>(gridSize).fill(false));
  const collision = CollisionMap.fromGrid(blockedGrid);

  const heightMap = Array.from({ length: gridSize }, () => new Array<number>(gridSize).fill(0));
  const waterMap = Array.from({ length: gridSize }, (_, y) =>
    Array.from({ length: gridSize }, (_, x) => blockedGrid[y]?.[x] ?? false),
  );

  // Render tile layers
  if (tmj && tilesetKey) {
    for (let li = 0; li < RENDER_LAYERS.length; li++) {
      const layerName = RENDER_LAYERS[li]!;
      const layer = tmj.layers.find((l) => l.name === layerName && l.type === "tilelayer");
      if (!layer?.data) continue;
      for (let ty = 0; ty < gridSize; ty++) {
        for (let tx = 0; tx < gridSize; tx++) {
          const tileId = layer.data[ty * gridSize + tx] ?? 0;
          if (tileId === 0) continue;
          const frameIndex = tileId - 1;
          const { sx, sy } = projection.worldToScreen(tx, ty);
          const img = scene.add.image(
            origin.sx + sx + halfW,
            origin.sy + sy,
            tilesetKey,
            frameIndex,
          );
          img.setOrigin(0.5, 0);
          img.setDepth(projection.depthKey(tx, ty) + li * 100);
        }
      }
    }
  }

  // Iso-only: dock decorations and ferry/bridge platform tiles
  if (isIso) {
    ensureDockTexture(scene);
    placeDockTiles(scene, origin, zoneConfig.transitions);
  }

  const container = scene.add.container(0, 0);

  return {
    map: {
      container,
      origin,
      heightMap,
      waterMap,
      collision,
      trees: [],
      decorations: [],
    },
    transitions: zoneConfig.transitions,
    spawnPoint: zoneGrid.spawn_point,
  };
}

/* ───── Iso dock texture (generated once) ───── */

function ensureDockTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(DOCK_TEXTURE_KEY)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Wooden platform — iso diamond with plank lines
  g.fillStyle(0x8a6a3a, 1);
  g.beginPath();
  g.moveTo(TILE.halfW, 0);
  g.lineTo(TILE.width, TILE.halfH);
  g.lineTo(TILE.halfW, TILE.height);
  g.lineTo(0, TILE.halfH);
  g.closePath();
  g.fillPath();

  // Plank lines
  g.lineStyle(1, 0x6a4a2a, 0.6);
  for (let i = 1; i < 4; i++) {
    const t = i / 4;
    const lx = TILE.halfW * (1 - t);
    const ly = TILE.halfH * t;
    g.lineBetween(lx, ly, lx + TILE.halfW, ly + TILE.halfH);
  }

  // Border
  g.lineStyle(1, 0x5a3a20, 0.8);
  g.beginPath();
  g.moveTo(TILE.halfW, 0);
  g.lineTo(TILE.width, TILE.halfH);
  g.lineTo(TILE.halfW, TILE.height);
  g.lineTo(0, TILE.halfH);
  g.closePath();
  g.strokePath();

  g.generateTexture(DOCK_TEXTURE_KEY, TILE.width, TILE.height);
  g.destroy();
}

/* ───── Iso dock tile placement ───── */

function placeDockTiles(
  scene: Phaser.Scene,
  origin: ScreenPos,
  transitions: ZoneTransition[],
): void {
  for (const tr of transitions) {
    const pos = tr.dockPosition ?? tr.bridgePosition;
    if (!pos) continue;
    const [dx, dy] = pos;
    const { sx, sy } = worldToScreen(dx, dy);
    const dock = scene.add.image(
      origin.sx + sx + TILE.halfW,
      origin.sy + sy,
      DOCK_TEXTURE_KEY,
    );
    dock.setOrigin(0.5, 0);
    dock.setDepth((dx + dy) * 1000);
  }
}
